import React, {useEffect, useMemo, useRef} from "react";
import {Pie} from "@ant-design/plots";
import {useTranslation} from "react-i18next";

const EMPTY_COLOR = "#e1e7f1";

// @ant-design/plots re-renders the whole chart on every React render (it builds a new onReady each time),
// which wipes the hover highlight. Memo keeps it from re-rendering when only the hover state changes.
const StablePie = React.memo(Pie);

// segments: [{key, label, value, color}]
// onHoverChange(key | null) fires when a slice is hovered / left
// highlightKey highlights a slice from outside (e.g. when its tile is hovered)
const DeviceHealthDonut = ({total, active, segments, onHoverChange, highlightKey}) => {
    const {t} = useTranslation();
    const activePercent = total > 0 ? Math.round((active / total) * 100) : 0;
    const hasData = segments.some(({value}) => Number(value) > 0);
    // Hovered status (slice or tile) replaces the total in the center
    const focused = hasData ? segments.find((item) => item.key === highlightKey) : null;
    const focusedValue = Number(focused?.value) || 0;

    // The chart redraws whenever its config changes, so keep the config stable across hover re-renders
    const segmentsRef = useRef(segments);
    const onHoverRef = useRef(onHoverChange);
    segmentsRef.current = segments;
    onHoverRef.current = onHoverChange;
    const segmentsKey = JSON.stringify(segments.map(({key, label, value, color}) => [key, label, value, color]));
    const chartRef = useRef(null);
    // Last key reported by the pie itself; echoing it back into G2 fights its own hover state
    const chartHoverKeyRef = useRef(null);
    // Key we pushed into G2 from outside (tile hover)
    const externalKeyRef = useRef(null);

    const config = useMemo(() => {
        const segmentList = segmentsRef.current;
        const data = hasData
            ? segmentList.map(({label, value}) => ({label, value: Number(value) || 0}))
            : [{label: "-", value: 1}];

        // onReady runs once for the plot's whole life, so read everything through refs.
        // Listen on the inner G2 chart: the plot wrapper re-emits events without the "element:" prefix.
        const handleReady = (plot) => {
            const chart = plot.chart || plot;
            chartRef.current = chart;
            const emitHover = (key) => {
                chartHoverKeyRef.current = key;
                if (onHoverRef.current) onHoverRef.current(key);
            };

            chart.on("element:pointerover", (event) => {
                const label = event.data?.data?.label;
                const segment = segmentsRef.current.find((item) => item.label === label);
                emitHover(segment ? segment.key : null);
            });
            chart.on("element:pointerout", () => emitHover(null));
            chart.on("plot:pointerleave", () => emitHover(null));
        };

        return {
            data,
            angleField: "value",
            colorField: "label",
            innerRadius: 0.74,
            autoFit: true,
            containerStyle: {width: "100%", height: "100%"},
            padding: 0,
            margin: 0,
            legend: false,
            label: false,
            tooltip: hasData,
            style: {radius: 4, inset: hasData ? 1.5 : 0},
            state: {inactive: {opacity: 0.35}},
            interaction: hasData ? {elementHighlight: true} : {},
            scale: {
                color: {
                    domain: data.map(({label}) => label),
                    range: hasData ? segmentList.map(({color}) => color) : [EMPTY_COLOR],
                },
            },
            onReady: handleReady,
        };
    }, [segmentsKey, hasData]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart || !hasData) return;
        // The change came from hovering the pie: G2 already shows it
        if (highlightKey === chartHoverKeyRef.current && externalKeyRef.current === null) return;

        const segment = segmentsRef.current.find((item) => item.key === highlightKey);
        if (segment) {
            externalKeyRef.current = segment.key;
            chart.emit("element:highlight", {data: {data: {label: segment.label}}});
        } else {
            externalKeyRef.current = null;
            chart.emit("element:unhighlight", {});
        }
    }, [highlightKey, hasData]);

    return (
        <div className="analysis__metrics__health-chart">
            <StablePie {...config}/>
            <div className="analysis__metrics__health-center">
                <span className="analysis__metrics__health-caption">{focused ? focused.label : t("common.total")}</span>
                <span className="analysis__metrics__health-total">
                    {(focused ? focusedValue : total).toLocaleString("en-US")}
                </span>
                {focused ? (
                    <span
                        className="analysis__metrics__health-pill analysis__metrics__health-pill--segment"
                        style={{'--pill-color': focused.color}}
                    >
                        {total > 0 ? Math.round((focusedValue / total) * 100) : 0}%
                    </span>
                ) : (
                    <span className="analysis__metrics__health-pill">
                        {t("analysis.numbersStats.health.activeShare", {percent: activePercent})}
                    </span>
                )}
            </div>
        </div>
    );
};

export default DeviceHealthDonut;
